/**
 * Artwork Drawer — capture.
 *
 * The only file that knows how the personalizer talks. It listens to the
 * events fishionist-personalizer.js broadcasts and turns them into stored
 * artworks. If the personalizer or the Worker ever changes shape, this is the
 * single file to update.
 *
 * Events consumed (all fire and forget, dispatched on `document`):
 *   fsh:artwork-generated  a new artwork came back from /generate
 *   fsh:artwork-composed   a background artwork was composited into a print file
 *   fsh:artwork-placement  the customer moved or resized the subject
 *
 * Work is deferred to a macrotask so the generation flow never waits on us.
 */
(function () {
  'use strict';

  // Resolved per event rather than at load time. The store may be loaded by the
  // drawer snippet, which can come later in the document, and no artwork event
  // can fire before the customer has interacted with the page anyway.
  function getStore() {
    return window.ArtworkDrawerStore || null;
  }

  var warned = false;

  // The personalizer's own root first, the artwork drawer second. The drawer
  // used to be the only host for these two attributes; it no longer renders on
  // the product pages, but the fallback stays so that putting it back is a
  // one-line Liquid revert with no asset deploy to coordinate.
  function config() {
    var root =
      document.getElementById('fsh-personalizer') || document.getElementById('artwork-drawer');
    return root ? root.dataset : null;
  }

  // The Poster Builder's catalogue, if this page carries one. Read once and
  // remembered: it is a static <script type="application/json"> block.
  var catalogue;

  function catalogueName(handle) {
    if (catalogue === undefined) {
      catalogue = null;
      var tag = document.querySelector('[data-fshb-styles]');
      if (tag) {
        try {
          catalogue = JSON.parse(tag.textContent) || null;
        } catch (e) {
          catalogue = null;
        }
      }
    }
    if (!catalogue || !handle) return '';
    for (var i = 0; i < catalogue.length; i++) {
      if (catalogue[i] && catalogue[i].handle === handle) return catalogue[i].name || '';
    }
    return '';
  }

  // The merchant's own label for the style wins, so a poster made here is called
  // exactly what the same style is called inside the sheet. Those two used to
  // disagree — the block label said "Watercolor" while this page derived
  // "Watercolor Art" from the product title — and now that both kinds of poster
  // sit side by side in one strip, the difference is on screen. Falls back to
  // the derived label for a style with no block, which is also what runs if the
  // catalogue is not on the page at all.
  function styleLabel(handle) {
    return catalogueName(handle) || (config() || {}).styleLabel || '';
  }

  function productTitle() {
    var cfg = config();
    return (cfg && cfg.productTitle) || '';
  }

  // Never block the caller: the personalizer dispatches these synchronously
  // while the customer is waiting on their artwork.
  function defer(fn) {
    setTimeout(function () {
      try {
        fn();
      } catch (e) {
        /* a storage failure must not surface to the customer */
      }
    }, 0);
  }

  function onGenerated(event) {
    var d = (event && event.detail) || {};
    var res = d.response || {};
    if (res.status !== 'ok' || !res.id) return report();

    defer(function () {
      var store = getStore();
      if (!store) return;

      var base = {
        id: res.id,
        createdAt: Date.now(),
        productId: d.productId || '',
        handle: d.productHandle || '',
        styleLabel: styleLabel(d.productHandle || ''),
        productTitle: productTitle()
      };

      if (res.mode === 'background' && res.subject_preview_url) {
        // No print file yet: the subject is composited onto its background only
        // when the customer commits, either on the page or from the drawer.
        store.save(
          Object.assign(base, {
            mode: 'background',
            thumbUrl: res.subject_preview_url,
            subjectUrl: res.subject_preview_url,
            printUrl: null,
            backgroundId: d.backgroundId || null,
            bgThumbUrl: d.backgroundThumb || null,
            placement: d.placement || { scale: 0.85, xPct: 50, yPct: 50 },
            subjectW: res.subject_w || null,
            subjectH: res.subject_h || null
          })
        );
        return;
      }

      if (!res.output_url) return report();

      // Plain artworks are complete the moment they are generated, so they can
      // be bought from the drawer even if the customer never accepted them.
      store.save(
        Object.assign(base, {
          mode: 'plain',
          thumbUrl: res.preview_url || res.output_url,
          printUrl: res.output_url,
          previewUrl: res.preview_url || null,
          originalUrl: res.original_url || null
        })
      );
    });
  }

  function onComposed(event) {
    var d = (event && event.detail) || {};
    if (!d.id || !d.outputUrl) return;

    defer(function () {
      var store = getStore();
      if (!store) return;

      store.update(d.id, {
        printUrl: d.outputUrl,
        previewUrl: d.previewUrl || d.outputUrl,
        thumbUrl: d.outputUrl,
        backgroundId: d.backgroundId || null,
        bgThumbUrl: d.backgroundThumb || null,
        placement: d.placement || null,
        composedAt: Date.now()
      });
    });
  }

  function onPlacement(event) {
    var d = (event && event.detail) || {};
    if (!d.id || !d.placement) return;

    defer(function () {
      var store = getStore();
      if (!store) return;

      var entry = store.get(d.id);
      if (!entry) return;

      var patch = {
        backgroundId: d.backgroundId || null,
        bgThumbUrl: d.backgroundThumb || null,
        placement: d.placement
      };

      // Moving the subject after it was composed makes the existing print file
      // stale. Drop it and go back to showing the subject on its background, so
      // the gallery keeps matching what the customer last arranged. The drawer
      // composes again at add to cart. This mirrors the personalizer, which
      // also re-locks its Add to cart button on any adjustment.
      if (entry.composedAt && entry.subjectUrl) {
        patch.printUrl = null;
        patch.previewUrl = null;
        patch.composedAt = null;
        patch.thumbUrl = entry.subjectUrl;
      }

      store.update(d.id, patch);
    });
  }

  // One warning is enough to point a developer at this file.
  function report() {
    if (warned || !window.console) return;
    warned = true;
    console.warn('[artwork-drawer] unrecognized generate payload');
  }

  document.addEventListener('fsh:artwork-generated', onGenerated);
  document.addEventListener('fsh:artwork-composed', onComposed);
  document.addEventListener('fsh:artwork-placement', onPlacement);
})();
